import posthog from './posthog';

type Empty = Record<string, never>;

// Five statuses cover the full wallet→chain lifecycle. `signed` means the
// user approved in the wallet and the hash was returned; `mined` and
// `reverted` are chain outcomes. `rejected` is user cancellation in the
// wallet, `error` is any other throw (rpc, estimate, network).
type TxStatus = 'signed' | 'rejected' | 'error' | 'mined' | 'reverted';

// Non-tx actions (network switch) never reach the chain, so they only use
// the pre-chain subset of statuses.
type NonTxStatus = 'signed' | 'rejected' | 'error';
// Which surface collected the waitlist e-mail — shared by the
// landing_signup_* events.
type SignupSource = 'form' | 'modal' | 'market_gate' | 'score_og';

export type Events = {
  // The moment a login gains user intent. `redirect` = the full-page OAuth
  // round trip; `switcher` = the account switcher's "Use another account"
  // entry or a failed silent switch falling back — both run the
  // interactive login redirect, not the switch endpoint.
  sso_login_started: { method: 'redirect' | 'switcher' };
  // A completed interactive login. `connector` stays a prop at the
  // constant 'sso': PostHog's ingested rows are immutable and carry other
  // connector values, and breakdowns-by-connector must keep matching.
  // Page loads by an already-logged-in user fire session_restored instead
  // — don't count restores as logins.
  auth_completed: { connector: 'sso' };
  session_restored: { connector: 'sso' };
  auth_failed: {
    connector: 'sso';
    // sso_callback_invalid = the SSO return leg failed local validation
    // (state mismatch, replayed/bookmarked callback URL, missing code).
    reason: 'user_cancelled' | 'backend_error' | 'sso_callback_invalid';
    // HTTP status on backend_error — separates 5xx outages from 4xx rejections.
    status?: number;
  };
  // Settings' "Edit on Santiment" click — leaves for the SSO account page
  // where an SSO-owned username/avatar is edited.
  sso_profile_edit_opened: Empty;
  // Account switcher (SSO sessions; its one surface is the landing
  // account chip): started fires on a listed-account click (the "Use
  // another account" entry instead fires
  // sso_login_started{method:'switcher'} — it runs the interactive
  // login redirect, not the switch endpoint). completed fires INSTEAD of
  // auth_completed — a switch mints a session but is not a login, and
  // counting it as one would inflate the login funnel. method 'instant' =
  // the backend already held a usable SSO session for the target;
  // 'redirect' = the prompt=none round trip completed. failed 'sso_error' /
  // 'missing_code' = the round trip returned an OAuth error / no code —
  // the flow then falls back to the interactive chooser when SSO discovery
  // resolves, else reports and stays; 'invite_required' = the target
  // account has no SanR access (the current session stays).
  account_switch_started: { via: 'landing' };
  account_switch_completed: { method: 'instant' | 'redirect' };
  account_switch_failed: {
    reason: 'invite_required' | 'backend_error' | 'sso_error' | 'missing_code';
  };
  // `user` = the Logout button; session_expired is a forced termination
  // and must not be read as churn.
  logout: { reason: 'user' | 'session_expired' };

  landing_viewed: {
    utm_source?: string | undefined;
    utm_medium?: string | undefined;
    utm_campaign?: string | undefined;
    utm_content?: string | undefined;
  };
  // Fired when the waitlist modal opens. `source: 'sign_in'` is the
  // invite-gated path (uninvited user completes Sign In) — the
  // sso_login_started → waitlist_modal_shown drop-off. `source:
  // 'join_button'` is the header Join button, which opens the modal
  // directly (no login flow). `address` rides on the sign_in source — the
  // uninvited account's SSO-known wallet address, when it has one
  // (highest-intent leads; no session is stored, so no identify happens).
  waitlist_modal_shown: { source: 'sign_in' | 'join_button'; address?: string };
  // The header's "Become a Score OG" entry (the OG inviter's invitees). Its
  // own event, not a waitlist_modal_shown source: the application carries a
  // rating the waitlist funnel has no notion of.
  score_og_modal_shown: Empty;
  // Sign-in intent on the landing. `source` separates the header button,
  // the invite welcome modal's Continue and the redeem popup's Unlock
  // access. `logged_in: true` means the click just navigates to /markets;
  // `false` starts the SSO login (sso_login_started fires next in that
  // branch).
  landing_signin_clicked: {
    logged_in: boolean;
    source: 'header' | 'invite_modal' | 'redeem_modal';
  };
  // Fired once per screen per pageview as the user scrolls the multi-screen
  // landing — scroll-depth funnel. `screen` is 1-based; `total` is the
  // variant's screen count, so the variants stay separable should they
  // diverge.
  landing_screen_viewed: { screen: number; total: number };
  // Outbound santiment.net link in the per-screen footers.
  landing_brand_clicked: Empty;
  // `source` splits the signup funnels: the landing's on-page email form,
  // the invite-gated WaitlistModal, and the market page's sign-up gate
  // forms.
  landing_email_started: {
    source: SignupSource;
  };
  // `social_provided` = the optional X/Telegram/trading-profile field was
  // filled — the lead-quality signal (an outreach-ready lead vs a bare email).
  landing_signup_submitted: {
    email_domain: string;
    source: SignupSource;
    social_provided: boolean;
  };
  landing_signup_succeeded: {
    email_domain: string;
    source: SignupSource;
    social_provided: boolean;
  };
  landing_signup_duplicate: {
    email_domain: string;
    source: SignupSource;
  };
  landing_signup_failed: {
    reason: 'non_ok' | 'network_error';
    status?: number;
    source: SignupSource;
  };
  landing_social_clicked: { network: 'telegram' | 'discord' | 'x' };
  // "Apply for early access" — jumps to the waitlist form screen. Arrival
  // there also fires landing_screen_viewed; this separates CTA-driven
  // arrivals from organic scrolling. `source`: the hero's above-the-fold
  // link, or the redeem popup's "No code?" exit.
  landing_apply_clicked: { source: 'hero' | 'redeem_modal' };
  // Above-the-fold "See FAQ" link — jumps to the FAQ screen. Arrival there
  // also fires landing_screen_viewed; this separates CTA-driven arrivals
  // from organic scrolling.
  landing_faq_clicked: Empty;
  // The hero's "Redeem invite" link — opens the popup for a code received
  // outside an invite link. Its Unlock access continues as
  // landing_signin_clicked{source:'redeem_modal'}; the backend verdict is
  // the invite-link funnel's invite_code_redeemed.
  landing_redeem_clicked: Empty;
  // Invite-link funnel: welcome modal shown on ?invite_code= arrival →
  // Continue (landing_signin_clicked{source:'invite_modal'}) starts the
  // SSO login → invite_code_redeemed carries the backend outcome.
  // `invalid` also means the invalid-invite modal was shown; success shows
  // up as auth_completed + app entry.
  landing_invite_modal_shown: { invite_code: string };
  invite_code_redeemed: {
    status: 'success' | 'invalid' | 'error';
    invite_code: string;
    // The inviter's issuer id — only known on success.
    invited_by?: string | undefined;
  };

  // `footer` = the mobile bottom nav.
  nav_clicked: {
    location: 'header' | 'profile_tabs' | 'footer';
    label: string;
  };
  // Help page (/help). Page mount; header entry clicks reuse nav_clicked
  // with label 'Help'.
  help_opened: Empty;
  // The "Contact us" card (mailto:support@scorearena.ai) — support intent.
  help_contact_clicked: Empty;
  // Expanding an FAQ accordion item on /help (click or a Ctrl+F beforematch
  // reveal). `section` disambiguates questions that repeat across groups
  // ("How much can I earn?").
  faq_question_opened: { section: string; question: string };
  // Ask AI block on /help. question_asked fires per submitted question;
  // `follow_up` = the block already showed a question/answer this pageview
  // (the server session keeps conversation context).
  help_ai_question_asked: {
    source: 'input' | 'popular_question' | 'retry';
    follow_up: boolean;
  };
  // Terminal outcome of one AI run the user actually saw end (a question
  // superseded by a newer one reports nothing). `tokens` is the backend's
  // spend for the run — only present on `done`.
  help_ai_answer_finished: {
    status: 'done' | 'stopped' | 'rate_limited' | 'error';
    tokens?: number;
  };
  // A related-topic chip under the AI answer — opens that FAQ item below
  // (which also fires faq_question_opened).
  help_ai_related_topic_clicked: { question: string };
  // AI assistant on every logged-in page (the desktop popup, the ≤767
  // sheet; the personal-agent chat). opened fires per open — `scope: 'page'`
  // from the desktop launcher or the ≤767 nav pill (the page context rides
  // on $pathname), `'market'` from the ≤767 market card's Ask AI button,
  // `'activity'` from the Ask AI of a market's whale-activity summary card
  // (`signal_id` = that market for both); the ask/finish pair mirrors the
  // help_ai_* semantics on this surface, with `source: 'suggested_question'`
  // = one of the scoped chips and `busy` = the dialog was still answering a
  // previous message (one answer per personal-agent dialog at a time).
  ai_widget_opened: {
    scope: 'page' | 'market' | 'activity';
    signal_id?: string;
  };
  ai_widget_question_asked: {
    source: 'input' | 'popular_question' | 'suggested_question' | 'retry';
    follow_up: boolean;
  };
  ai_widget_answer_finished: {
    status: 'done' | 'stopped' | 'busy' | 'rate_limited' | 'error';
    tokens?: number;
  };
  // The market-card AI panel (the desktop card's Ask AI button, the
  // personal-agent chat). opened fires per panel open; the ask/finish pair
  // mirrors ai_widget_* semantics; `suggestion` = one of the panel's prompt
  // chips; `busy` = the dialog was still answering a previous message (one
  // answer per personal-agent dialog at a time).
  market_ai_opened: { asset: string };
  market_ai_question_asked: {
    source: 'input' | 'suggestion' | 'retry';
    follow_up: boolean;
  };
  market_ai_answer_finished: {
    status: 'done' | 'stopped' | 'busy' | 'rate_limited' | 'error';
    tokens?: number;
  };
  // The History view inside an AI panel (journal entries + past chats).
  ai_history_opened: { surface: 'widget' | 'market' };
  // Opening a past conversation from the history list.
  ai_history_dialog_opened: Empty;
  // The message menu's journal actions inside an AI panel: saving a user
  // message as a trader-journal note / removing that note. `surface` = which
  // panel; the market panel files the note under its pair.
  ai_message_saved_to_journal: { surface: 'widget' | 'market' };
  ai_message_removed_from_journal: { surface: 'widget' | 'market' };
  // A confirmed Edit of a sent message in an AI panel: the backend rewrites
  // it, deletes every later message and answers it again.
  ai_message_edited: { surface: 'widget' | 'market' };
  // Own-profile Invites tab (/user/:username/invites, gated to score-team
  // testers — the shared `testers` flag; tab clicks reuse nav_clicked with
  // label 'Invites'). Tab mount.
  invites_opened: Empty;
  // POST /v1/invite-codes/generate outcome. note_provided = the optional
  // note field was filled at generate time.
  invite_generated: { status: 'success' | 'error'; note_provided: boolean };
  // Copying an invite link from the Invites tab's active-links list.
  invite_link_copied: Empty;
  // Saving a note edit on an existing code (PUT /v1/invite-codes/{id}/note).
  invite_note_saved: { status: 'success' | 'error' };
  error_page_shown: Empty;
  // Gateway-outage banner: shown fires once per outage window (first
  // 502/503 response detected → queries paused, banner up); recovered
  // fires when every down backend's health probe succeeds again.
  // duration_seconds = shown → recovered.
  backend_unavailable_shown: Empty;
  backend_unavailable_recovered: { duration_seconds: number };
  // Stale-bundle banner: shown fires once per tab life, when a newer
  // release is detected; reload_clicked = its Reload.
  release_banner_shown: Empty;
  release_reload_clicked: Empty;
  // Logged-out (or uninvited) visit to a protected app route, bounced to
  // the landing. `path` = the route the user actually wanted.
  app_bounce_to_landing: { path: string };

  // Prediction REST events. `status` is a REST lifecycle
  // (submitted → success/error), not an on-chain tx status.
  // stop_loss/take_profit are omitted (not '') when the field is blank, so
  // "property is set" filters mean "has SL/TP". `signal_id` is the market id.
  prediction_create: {
    status: 'submitted' | 'success' | 'error';
    signal_id: string;
    symbol: string;
    leverage: number;
    stop_loss?: number;
    take_profit?: number;
  };
  // SL/TP save on an OPEN position via the Edit Signal modal (profile
  // Active tab / voting panel). The modal saves both fields at once — no
  // per-field prop.
  prediction_price_edit: {
    status: 'submitted' | 'success' | 'error';
    prediction_id: string;
    source: 'voting_panel' | 'profile';
  };
  prediction_close: {
    status: 'submitted' | 'success' | 'error';
    prediction_id: string;
    source: 'voting_panel' | 'profile';
  };
  // Undo inside the profile tab's 5s close grace window — the close request
  // never fires (prediction_close covers only fired requests, submitted →
  // success/error).
  prediction_close_undone: { prediction_id: string; source: 'profile' };
  // Pending (trigger/limit) order management — the Edit Signal modal saves
  // trigger/leverage/SL/TP on a `created` prediction; order_cancel cancels
  // one before it fires.
  order_edit: {
    status: 'submitted' | 'success' | 'error';
    prediction_id: string;
    source: 'voting_panel' | 'profile';
  };
  order_cancel: {
    status: 'submitted' | 'success' | 'error';
    prediction_id: string;
    source: 'voting_panel' | 'profile';
  };
  // Prediction funnel — intent steps before the `prediction_create` outcome,
  // so drop-off between "clicked a side" and "confirmed" is measurable.
  prediction_vote_clicked: {
    signal_id: string;
    asset: string;
    vote: 'agree' | 'disagree';
  };
  prediction_panel_abandoned: { signal_id: string; asset: string };
  // Tx lifecycle events — one per flow, `status` carries the lifecycle
  // stage. sendAndTrack fires these automatically; call sites should not
  // invoke track() for tx flows directly.
  sell_keys: { status: TxStatus; issuer: string };
  withdraw: {
    status: TxStatus;
    id: 'legacy-storage' | 'point-rewards' | 'league-rewards' | 'zk-fees';
  };
  network_switch: { status: NonTxStatus };
  // A wallet-bound action (stake, unstake, swap) stopped by the wallet
  // guard: no_wallet = a walletless account (terminal — a notification),
  // not_connected / wrong_wallet = the connect-this-wallet modal opened.
  wallet_action_blocked: {
    reason: 'no_wallet' | 'not_connected' | 'wrong_wallet';
  };

  withdraw_popup_opened: Empty;

  // Notifications. `opened` fires only when the dropdown opens (not on
  // toggle-close); `marked_all_read` fires after the PUT succeeds;
  // `clicked` = a notification's username link (re-engagement signal).
  notifications_opened: Empty;
  notifications_marked_all_read: Empty;
  notification_clicked: Empty;

  watchlist_toggled: {
    issuer: string;
    action: 'add' | 'remove';
    source: 'watchlist_tab' | 'community_popup' | 'profile_header';
  };

  note_opened: { variant: string };
  note_edited: Empty;

  // Telegram bot. `link_clicked` = the Activate button that opens the t.me
  // deep link; `disabled` fires after the PUT succeeds.
  tg_bot_link_clicked: Empty;
  tg_bot_disabled: Empty;
  // Settings → Notifications, after the POST succeeds: one issuer's
  // Telegram toggle, or the Select all / Unselect all confirmation.
  notification_setting_toggled: { scope: 'issuer' | 'all'; enabled: boolean };

  // `value` is the picked option, or 'cleared' when a filter is reset.
  markets_filter_applied: {
    filter_type: 'scope' | 'status' | 'kols' | 'assets' | 'sort';
    value: string;
  };
  profile_viewed: { username: string; is_own: boolean };
  // The ≤767 profile header's Share button: `native` = navigator.share took
  // the profile URL, `copy_link` = the clipboard fallback where
  // navigator.share is missing.
  profile_shared: {
    username: string;
    is_own: boolean;
    target: 'native' | 'copy_link';
  };
  profile_predictions_tab_viewed: { tab: 'active' | 'closed' | 'orders' };
  prediction_detail_opened: {
    prediction_id: string;
    is_active: boolean;
    source: 'profile' | 'community_votes';
  };
  // Fires on each infinite-scroll auto-load (`has_more: true`) and once per
  // mount when the user hits the true end of the feed (`has_more: false`).
  feed_reached_bottom: { page: string; has_more: boolean };
  profile_settings_saved: {
    username_changed: boolean;
    description_changed: boolean;
  };
  // The Settings modal's tabs — a change alone, never a re-click.
  settings_tab_changed: { tab: 'profile' | 'notifications' };
  // 5-year API token minted via auth/refresh (Settings → Advanced).
  api_key_generated: Empty;
  // X account linking: started = the Link button (OAuth handoff),
  // completed = the linked handle arrived on the profile.
  twitter_link_started: Empty;
  twitter_link_completed: Empty;
  twitter_unlinked: Empty;
  market_tweet_interaction: {
    action:
      | 'open_post'
      | 'open_kol_profile'
      | 'see_full'
      | 'open_tweet_media'
      | 'open_curator_image'
      | 'expand_tweet'
      | 'collapse_tweet'
      | 'show_original'
      | 'show_translation';
    signal_id: string;
    kol_handle: string;
    asset: string;
  };
  // Standalone market page (/markets/:id and the short /m/:ref share
  // link). Fires once auth state is known — logged_in separates
  // shared-link visitors from in-app navigation — and, on /m/ links, once
  // resolution settles: signal_id is the resolved full id, or the raw ref
  // segment when nothing matches (dead and malformed shared links stay
  // observable under it).
  market_page_viewed: { signal_id: string; logged_in: boolean };
  // The card's share affordance: opened = the dropdown (desktop) / bottom
  // sheet (mobile) was opened; shared = a target was picked. `copy_link`
  // and `native` (navigator.share) exist on both surfaces; the app targets
  // are mobile-sheet-only.
  market_share_opened: { signal_id: string };
  market_shared: {
    signal_id: string;
    target:
      'copy_link' | 'native' | 'messages' | 'whatsapp' | 'telegram' | 'gmail';
  };
  // Logged-out gate on the market page: an action that needs an account
  // showed the sign-up modal. `vote` rides along on the vote source.
  // `signal_id` is absent on nav-gate opens (the chrome doesn't know the
  // market); `ask_ai` = the card's Ask AI button; `follow` = a whale
  // row's Follow.
  market_gate_shown: {
    source: 'vote' | 'nav' | 'explore_more' | 'ask_ai' | 'follow';
    vote?: 'agree' | 'disagree';
    signal_id?: string;
  };
  // Sign-in intent on the market page — the header Sign In button or a gate
  // modal's CTA. sso_login_started fires next.
  market_signin_clicked: { source: 'header' | 'gate_modal' };
  // The ≤767 card's expand button — the fullscreen landscape chart layer.
  market_chart_fullscreen_opened: { signal_id: string; asset: string };
  // The card's Explore Activity panel (the whale wallets; the feed or the
  // market page rides on $pathname). opened and
  // closed bracket one open, duration_seconds the whole seconds between.
  // The tab event fires on a change alone, never on a re-click of the
  // active tab. wallet_expanded = a whale row opened into its details by a
  // click (the top row stands open on arrival, uncounted); `position` is
  // its 1-based rank in the list shown. summary_finished =
  // one generation of the 24h AI summary card (never a cache hit):
  // `rate_limited` is the account's agent budget answering 429, `error`
  // any other failure or an empty answer, `tokens` the stream's done-frame
  // count. ask_ai_clicked = the card's Ask AI, which hands the drawer over
  // to the AI widget's activity chat (ai_widget_opened fires too).
  // follow_clicked = a wallet row's "Follow" by a logged-in viewer, a
  // demand probe: no alert exists yet, so the click opens the whale-alerts
  // "coming soon" modal, whose buttons report through alert_modal_answered
  // (`notify` is the opt-in list, kept in PostHog alone; `never` hides the
  // Follow buttons in that browser; `dismissed` = Escape or the dim). A
  // logged-out click opens the sign-up gate instead and fires
  // market_gate_shown with source `follow`, never follow_clicked.
  // wallet_explorer_opened = a wallet address followed out to its
  // Hypurrscan page, the Hyperliquid explorer.
  market_activity_opened: { signal_id: string; asset: string };
  market_activity_closed: {
    signal_id: string;
    asset: string;
    duration_seconds: number;
  };
  market_activity_whale_tab_changed: {
    signal_id: string;
    asset: string;
    tab: 'realized' | 'unrealized' | 'total';
  };
  market_activity_wallet_expanded: {
    signal_id: string;
    asset: string;
    address: string;
    direction: 'long' | 'short';
    position: number;
  };
  market_activity_summary_finished: {
    signal_id: string;
    asset: string;
    status: 'done' | 'rate_limited' | 'error';
    tokens?: number;
  };
  market_activity_ask_ai_clicked: { signal_id: string; asset: string };
  market_activity_follow_clicked: {
    signal_id: string;
    asset: string;
    address: string;
    direction: 'long' | 'short';
  };
  market_activity_alert_modal_answered: {
    signal_id: string;
    asset: string;
    address: string;
    direction: 'long' | 'short';
    answer: 'notify' | 'later' | 'never' | 'dismissed';
  };
  market_activity_wallet_explorer_opened: {
    signal_id: string;
    asset: string;
    address: string;
    direction: 'long' | 'short';
  };

  // KOL leaderboard (/kols) sort pills: 'score' is the win rate the list
  // ranks on by default, 'predictions' the market count.
  kols_sort_changed: { by: 'score' | 'predictions'; dir: 'asc' | 'desc' };
  kols_search: { query_length: number };
  kols_page_changed: { page: number };
  // A KOL profile opened (/kol/:handle) — the page is public, so
  // logged_in separates visitors arriving from a shared market link.
  kol_profile_viewed: { kol_handle: string; logged_in: boolean };
  // The profile's "#N in Leaderboard" button, back to that KOL's page.
  kol_leaderboard_clicked: { kol_handle: string; rank: number };
  // The profile's "Visit KOL's X profile" link — leaving for X.
  kol_x_profile_clicked: { kol_handle: string };
  // The profile's top-level section tabs (Overview / DNA).
  kol_profile_tab_changed: { tab: 'overview' | 'dna' };
  // The profile's prediction-history tabs.
  kol_history_tab_changed: { tab: 'active' | 'closed' };
  // A market row on a KOL profile, active or closed, opened into its
  // detail modal.
  kol_market_opened: { kol_handle: string };
  // The market modal's outbound link to the KOL's post on X.
  kol_market_link_clicked: Empty;
  // The profile's DNA tab. The chart's pair menu — a change alone; the
  // most-called pair it opens on is not counted. A tweet card's outbound
  // links: the post on X, or its media in the lightbox. The tweet history
  // opened past its four-card teaser, and paged (1-based).
  kol_dna_pair_changed: { pair: string };
  kol_dna_tweet_link_clicked: { target: 'post' | 'media' };
  kol_dna_history_expanded: Empty;
  kol_dna_history_page_changed: { page: number };

  qualification_filter_changed: {
    status: 'all' | 'eligible' | 'not_eligible' | 'funded';
  };
  qualification_sort_changed: {
    by: 'stake' | 'accuracy';
    dir: 'asc' | 'desc';
  };
  qualification_search: { query_length: number };
  qualification_page_changed: { page: number };
  // Candidate info/medals popup open, from the identity (avatar/name) —
  // hover-intent on desktop rows, tap on mobile cards.
  qualification_candidate_popup_opened: {
    kol_address: string;
    position: number;
  };
  // Profile click-through from Community: the popup's "View full profile"
  // link and the desktop row identity itself — same intent, one event.
  qualification_profile_opened: { kol_address: string };
  // The "Current Arena Providers" card's expand/collapse toggle.
  qualification_providers_toggled: { expanded: boolean };
  // The Funded tab's "Go to Arena to see strategies" click-through.
  qualification_go_to_arena_clicked: Empty;

  // Click-through to the profile Stakes tab (Backers view).
  backers_list_opened: {
    kol_address: string;
    kol_username: string | null;
  };
  stake_modal_opened: {
    kol_address: string;
    kol_username: string | null;
    source: 'community' | 'profile' | 'stakes_tab';
  };
  // How the user landed in the "Get SAN to Stake" swap view:
  // auto_zero_balance = auto-routed on open (no SAN at all),
  // insufficient_balance = typed more than they hold,
  // manual = clicked the action with an empty amount.
  stake_get_san_entered: {
    trigger: 'auto_zero_balance' | 'insufficient_balance' | 'manual';
  };
  stake_approve_attempted: { status: TxStatus; kol_address: string };
  stake_attempted: {
    status: TxStatus;
    kol_address: string;
    amount: string;
  };
  unstake_modal_opened: {
    kol_address: string;
    kol_username: string | null;
  };
  unstake_attempted: {
    status: TxStatus;
    kol_address: string;
    stake_id: number;
    amount: string;
  };

  arena_section_expanded: {
    section: 'top_markets' | 'top_stakers';
    issuer_address: string;
  };
  // Profile click-throughs from the Arena surfaces — the page's primary
  // success metric (driving discovery of funded SPs).
  arena_profile_clicked: {
    surface: 'card_header' | 'newcomer' | 'top_staker';
  };
  // Equity Curve legend toggle — hiding curves rescales the chart to the
  // remaining strategies.
  arena_curve_toggled: { username: string; action: 'hide' | 'show' };
  // The desktop Compare users panel's Apply — the set of strategies the
  // chart and the table then show (the default is the top three by PnL).
  arena_compare_applied: { count: number; usernames: string[] };
  // The desktop Equity Curve's timeframe select.
  arena_timeframe_changed: { timeframe: '12m' | '6m' | '3m' | '30d' | '2w' };

  // Paper strategies — the profile's Strategies tab and the market card's
  // Create a trade modal, on the Arena API's strategy and paper-trading
  // endpoints. `status` is the REST lifecycle (submitted → success/error),
  // as prediction_create. `source` separates the profile's trade panel from
  // the market modal, whose orders also carry the market as `signal_id`.
  strategy_create: {
    status: 'submitted' | 'success' | 'error';
    type: 'manual' | 'template';
    mode?: 'simple' | 'advanced';
  };
  paper_order_place: {
    status: 'submitted' | 'success' | 'error';
    strategy_id: string;
    symbol: string;
    side: 'buy' | 'sell';
    type: 'market' | 'limit';
    source: 'profile' | 'market';
    signal_id?: string;
  };
  // The positions table's Close all → Market: a close-position market order
  // for the whole position.
  paper_position_close: {
    status: 'submitted' | 'success' | 'error';
    strategy_id: string;
    symbol: string;
  };
  paper_position_tpsl_edit: {
    status: 'submitted' | 'success' | 'error';
    strategy_id: string;
  };
  paper_order_cancel: {
    status: 'submitted' | 'success' | 'error';
    strategy_id: string;
  };
  // The market card's Trade button, which opens the Create a trade modal
  // (shown to a viewer holding an active paper strategy).
  paper_trade_modal_opened: { signal_id: string; asset: string };

  profile_stakes_view_changed: { view: 'backers' | 'portfolio' };

  // `reverted` is deliberately absent from swap statuses — LiFi's
  // executeRoute throws on an on-chain revert, so reverts land in 'error'.
  // source: 'header' = the header SanSwapPill. 'profile' is dormant — no
  // profile-side swap entry exists to fire it.
  swap_modal_opened: { source: 'header' | 'profile' };
  swap_direction_flipped: { direction: 'buy' | 'sell' };
  swap_quote_failed: { reason: string };
  swap_attempted: {
    status: 'signed' | 'rejected' | 'error' | 'mined';
    from_symbol: string;
    to_symbol: string;
    from_amount: string;
    // 'profile' is dormant — no call site fires it.
    context: 'header' | 'stake' | 'profile';
  };

  // Onboarding tour funnel. welcome_shown fires once per appearance —
  // `auto` = the login auto-show, `checklist` = the Arena Setup "Complete"
  // re-entry. step_viewed fires once per (path, index) per tour run (the
  // route-pause resume doesn't re-fire). skipped carries no path when the
  // welcome ✕ was the exit. finished = a path reached the "Welcome on
  // board!" card (also latches the onboarding_completed tutorial).
  onboarding_welcome_shown: { source: 'auto' | 'checklist' };
  onboarding_path_chosen: { path: 'trader' | 'staker' };
  onboarding_step_viewed: {
    path: 'trader' | 'staker';
    step_id: string;
    index: number;
  };
  onboarding_skipped: {
    path?: 'trader' | 'staker' | undefined;
    step_index?: number | undefined;
  };
  onboarding_finished: { path: 'trader' | 'staker' };
  onboarding_finish_action: { action: 'get_started' | 'see_other_tour' };
  // Arena Setup checklist: dropdown opened (percent at open time) + every
  // tutorial-key latch (POST /v1/issuer/tutorials success) — includes
  // first_signal/first_stake/eligible reconciles and arena_setup_finished.
  arena_setup_opened: { percent: number };
  tutorial_marked: { key: string };
};

type EventsWithEmptyProps = {
  [K in keyof Events]: Events[K] extends Empty ? K : never;
}[keyof Events];

type EventsWithProps = Exclude<keyof Events, EventsWithEmptyProps>;

export function track(event: EventsWithEmptyProps): void;
export function track<E extends EventsWithProps>(
  event: E,
  props: Events[E],
): void;
export function track<E extends keyof Events>(
  event: E,
  props?: Events[E],
): void {
  posthog.capture(event, props);
}
